import { useMemo, useState, type MouseEvent } from 'react';
import type { DocumentPageSchema } from '@avisoma/shared';
import { ADTable } from '../../components/reconstructedDocument/extractionTables/ADTable';
import { SBTable } from '../../components/reconstructedDocument/extractionTables/SBTable';
import { LLPTable } from '../../components/reconstructedDocument/extractionTables/LLPTable';
import { PartMovementTable } from '../../components/reconstructedDocument/extractionTables/PartMovementTable';
import { EngineEventsTable } from '../../components/reconstructedDocument/extractionTables/EngineEventsTable';
import { LDNDTable } from '../../components/reconstructedDocument/extractionTables/LDNDTable';
import { ARCTable } from '../../components/reconstructedDocument/extractionTables/ARCTable';
import { BillOfSaleTable } from '../../components/reconstructedDocument/extractionTables/BillOfSaleTable';
import { QECTable } from '../../components/reconstructedDocument/extractionTables/QECTable';
import { BladeTable } from '../../components/reconstructedDocument/extractionTables/BladeTable';
import { NisTable } from '../../components/reconstructedDocument/extractionTables/NisTable';
import { ThrustConversionTable } from '../../components/reconstructedDocument/extractionTables/ThrustConversionTable';
import {
  PmaDerDeclarationsTable,
  PmaDerPartsTable,
  presentPmaDerScalarEntries,
} from '../../components/reconstructedDocument/extractionTables/PmaDerTables';
import { EngineMovementTable } from '../../components/reconstructedDocument/extractionTables/EngineMovementTable';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { presentBillOfSaleRows } from '../../components/reconstructedDocument/extractionTables/billOfSalePresentation';
import { presentWorkscopeFields } from '../../components/reconstructedDocument/extractionTables/workscopePresentation';
import { BorescopeInspectionCard } from './BorescopeInspectionCard';
import { RepairRecordCard } from './RepairRecordCard';
import { FlightLogRecordCard } from './FlightLogRecordCard';
import { RemovalTagCard } from './RemovalTagCard';
import { ConcessionRecordCard } from './ConcessionRecordCard';
import { AdTextReferenceCard } from './AdTextReferenceCard';
import { ExtractionWarnings } from './ExtractionWarnings';
import { DocumentHeadersCard } from './DocumentHeadersCard';

function cellTextFromEvent(event: MouseEvent<HTMLElement>): string | null {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  const cell = target.closest('td, dd');
  if (!cell || !event.currentTarget.contains(cell)) return null;
  const text = cell.textContent?.trim() ?? '';
  if (!text || text === '—') return null;
  return text;
}

interface DataExtractionResultsProps {
  page: DocumentPageSchema;
  onHoverText?: (text: string | null) => void;
}

export function DataExtractionResults({ page, onHoverText }: DataExtractionResultsProps) {
  const [inspected, setInspected] = useState<{ title: string; data: unknown } | null>(null);
  const inspect = (title: string) => (row: unknown) => setInspected({ title, data: row });

  // `utilisationRecord` is an object, so the auto scalar-entries block below skips
  // it. Flatten its present scalars into label/value pairs for a bespoke card.
  const utilisationEntries = useMemo(() => {
    const record = page.processing?.dataExtraction?.result?.utilisationRecord;
    if (!record) return [];
    const labels: Record<string, string> = {
      reportDate: 'Report Date',
      latestFlightDate: 'Latest Flight Date',
      earliestFlightDate: 'Earliest Flight Date',
      latestTAH: 'Total Air Hours (TAH)',
      latestTAC: 'Total Air Cycles (TAC)',
      aircraftRegistration: 'Aircraft Registration',
    };
    return Object.entries(labels)
      .map(([key, label]) => [label, record[key as keyof typeof record]] as const)
      .filter(([, value]) => value != null)
      .map(([label, value]) => [label, String(value)] as const);
  }, [page.processing?.dataExtraction?.result?.utilisationRecord]);

  // Same story as `utilisationRecord`: `engineWorkscope` is an object, so the scalar
  // block below skips it. Flatten its present scalars for a bespoke card — the
  // `workscopeLevelRaw` / `dateRaw` / `dateLabel` evidence lanes stay out of the card
  // and remain visible in the page's debug payload.
  const workscopeEntries = useMemo(() => {
    const record = page.processing?.dataExtraction?.result?.engineWorkscope;
    return record ? presentWorkscopeFields(record) : [];
  }, [page.processing?.dataExtraction?.result?.engineWorkscope]);

  const pmaDerStatus = page.processing?.dataExtraction?.result?.pmaDerStatus;
  const pmaDerEntries = useMemo(() => presentPmaDerScalarEntries(pmaDerStatus), [pmaDerStatus]);
  const pmaDerDeclarations = pmaDerStatus?.declarations ?? [];
  const pmaDerParts = pmaDerStatus?.parts ?? [];

  const extractionResult = page.processing?.dataExtraction?.result;
  const billOfSaleRows = useMemo(
    () =>
      presentBillOfSaleRows(
        extractionResult?.billOfSaleEvents ?? [],
        extractionResult?.isDocumentSigned,
      ),
    [extractionResult?.billOfSaleEvents, extractionResult?.isDocumentSigned],
  );
  const scalarEntries = extractionResult
    ? Object.entries(extractionResult).filter(([, value]) => typeof value !== 'object')
    : [];
  const hasScalars = scalarEntries.length > 0;

  if (!extractionResult) return null;

  return (
    <div
      className="space-y-6"
      onMouseOver={(event) => onHoverText?.(cellTextFromEvent(event))}
      onMouseLeave={() => onHoverText?.(null)}
    >
      {/* Scalar extraction fields */}
      {hasScalars && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Extraction Fields</h2>
          </div>
          <div className="px-4 py-4">
            <dl className="grid grid-cols-3 gap-4">
              {scalarEntries.map(([key, value]) => (
                <div key={key}>
                  <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{key}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{String(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* `documentHeaders` is a nested object, so the scalar block above skips
          it. Render the present category candidates as labelled fields. */}
      <DocumentHeadersCard headers={extractionResult.documentHeaders} />

      {/* Sanitiser strips describe the page's latest extraction run as a whole. A page
          carries one category, so exactly one table renders below and there is no
          ambiguity about which rows a warning refers to. The mount is deliberately
          independent of any table: the case that matters most is every row being
          dropped, which leaves no table to hang the warning under. Renders `null` when
          there are no warnings. */}
      <ExtractionWarnings warnings={extractionResult.extractionWarnings} />

      {/* Extraction event tables — dense bespoke grids, each with CSV export.
          No source-page column: every row belongs to this one page, so no
          `pageIdToPosition` is passed. */}
      {extractionResult.engineEvents && extractionResult.engineEvents.length > 0 && (
        <ExtractionTableCard label="Engine Events" rows={extractionResult.engineEvents} csvTitle="Engine Events">
          <EngineEventsTable
            rows={extractionResult.engineEvents}
            onInspect={inspect('Engine Event row')}
            warningDates={extractionResult.extractionWarnings
              ?.filter((warning) => warning.code === 'chronology-conflict')
              .map((warning) => warning.date)}
          />
        </ExtractionTableCard>
      )}

      {extractionResult.llpStatusEvents && extractionResult.llpStatusEvents.length > 0 && (
        <ExtractionTableCard label="LLP Status" rows={extractionResult.llpStatusEvents} csvTitle="LLP Status">
          <LLPTable rows={extractionResult.llpStatusEvents} onInspect={inspect('LLP Status row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.partMovementEvents && extractionResult.partMovementEvents.length > 0 && (
        <ExtractionTableCard
          label="Part Movement"
          rows={extractionResult.partMovementEvents}
          csvTitle="Part Movement"
        >
          <PartMovementTable rows={extractionResult.partMovementEvents} onInspect={inspect('Part Movement row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.adStatusEvents && extractionResult.adStatusEvents.length > 0 && (
        <ExtractionTableCard label="AD Status" rows={extractionResult.adStatusEvents} csvTitle="AD Status">
          <ADTable rows={extractionResult.adStatusEvents} onInspect={inspect('AD Status row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.sbStatusEvents && extractionResult.sbStatusEvents.length > 0 && (
        <ExtractionTableCard label="SB Status" rows={extractionResult.sbStatusEvents} csvTitle="SB Status">
          <SBTable rows={extractionResult.sbStatusEvents} onInspect={inspect('SB Status row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.ldndStatusEvents && extractionResult.ldndStatusEvents.length > 0 && (
        <ExtractionTableCard label="LDND" rows={extractionResult.ldndStatusEvents} csvTitle="LDND">
          <LDNDTable rows={extractionResult.ldndStatusEvents} onInspect={inspect('LDND row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.arcEvents && extractionResult.arcEvents.length > 0 && (
        <ExtractionTableCard label="ARC" rows={extractionResult.arcEvents} csvTitle="ARC">
          <ARCTable arcEvents={extractionResult.arcEvents} onInspect={inspect('ARC row')} />
        </ExtractionTableCard>
      )}

      {billOfSaleRows.length > 0 && (
        <ExtractionTableCard label="Bill of Sale" rows={billOfSaleRows} csvTitle="Bill of Sale">
          <BillOfSaleTable rows={billOfSaleRows} onInspect={inspect('Bill of Sale row')} />
        </ExtractionTableCard>
      )}

      {/* QEC / LRU list rows have a wide bespoke shape — reuse the entity view's
          QECTable (no source-page column on the page view). */}
      {extractionResult.qecListEvents && extractionResult.qecListEvents.length > 0 && (
        <ExtractionTableCard label="QEC / LRU List" rows={extractionResult.qecListEvents} csvTitle="QEC / LRU List">
          <QECTable rows={extractionResult.qecListEvents} onInspect={inspect('QEC / LRU row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.bladeStatusEvents && extractionResult.bladeStatusEvents.length > 0 && (
        <ExtractionTableCard label="Blades & nozzles" rows={extractionResult.bladeStatusEvents} csvTitle="Blades & nozzles">
          <BladeTable rows={extractionResult.bladeStatusEvents} onInspect={inspect('Blade / nozzle row')} />
        </ExtractionTableCard>
      )}

      {utilisationEntries.length > 0 && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Utilisation Record</h2>
          </div>
          <div className="px-4 py-4">
            <dl className="grid grid-cols-3 gap-4">
              {utilisationEntries.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {workscopeEntries.length > 0 && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Engine Workscope</h2>
          </div>
          <div className="px-4 py-4">
            <dl className="grid grid-cols-3 gap-4">
              {workscopeEntries.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {extractionResult.borescopeInspection && <BorescopeInspectionCard record={extractionResult.borescopeInspection} />}

      {extractionResult.repairRecord && <RepairRecordCard record={extractionResult.repairRecord} />}

      {extractionResult.nisStatementEvents && extractionResult.nisStatementEvents.length > 0 && (
        <ExtractionTableCard
          label="NIS Statement"
          rows={extractionResult.nisStatementEvents}
          csvTitle="NIS Statement"
        >
          <NisTable rows={extractionResult.nisStatementEvents} onInspect={inspect('NIS Statement row')} />
        </ExtractionTableCard>
      )}

      {extractionResult.thrustConversionEvents && extractionResult.thrustConversionEvents.length > 0 && (
        <ExtractionTableCard
          label="Thrust Rating Change Records"
          rows={extractionResult.thrustConversionEvents}
          csvTitle="Thrust Rating Change Records"
        >
          <ThrustConversionTable
            rows={extractionResult.thrustConversionEvents}
            onInspect={inspect('Thrust Conversion row')}
          />
        </ExtractionTableCard>
      )}

      {(pmaDerEntries.length > 0 || pmaDerDeclarations.length > 0 || pmaDerParts.length > 0) && (
        <>
          {pmaDerEntries.length > 0 && (
            <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
                <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">PMA/DER Status</h2>
              </div>
              <div className="px-4 py-4">
                <dl className="grid grid-cols-3 gap-4">
                  {pmaDerEntries.map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                      <dd
                        className={
                          label === 'ESNs'
                            ? 'mt-1 text-sm font-mono text-gray-900 dark:text-zinc-100'
                            : 'mt-1 text-sm text-gray-900 dark:text-zinc-100'
                        }
                      >
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          )}
          {pmaDerDeclarations.length > 0 && (
            <ExtractionTableCard
              label="PMA/DER Declarations"
              rows={pmaDerDeclarations}
              csvTitle="PMA-DER Declarations"
            >
              <PmaDerDeclarationsTable
                rows={pmaDerDeclarations}
                onInspect={inspect('PMA/DER declaration')}
              />
            </ExtractionTableCard>
          )}
          {pmaDerParts.length > 0 && (
            <ExtractionTableCard label="PMA/DER Parts" rows={pmaDerParts} csvTitle="PMA-DER Parts">
              <PmaDerPartsTable rows={pmaDerParts} onInspect={inspect('PMA/DER part')} />
            </ExtractionTableCard>
          )}
        </>
      )}

      {extractionResult.engineMovementEvents && extractionResult.engineMovementEvents.length > 0 && (
        <ExtractionTableCard
          label="Engine Install/Removal Job Card"
          rows={extractionResult.engineMovementEvents}
          csvTitle="Engine Install/Removal Job Card"
        >
          <EngineMovementTable
            rows={extractionResult.engineMovementEvents}
            onInspect={inspect('Engine Movement row')}
          />
        </ExtractionTableCard>
      )}

      {extractionResult.techLogRecords?.map((record, index) => (
        <FlightLogRecordCard key={`tech-log-${index}`} title="Tech Log Sheet (flight totals)" record={record} />
      ))}

      {extractionResult.ferryFlightRecords?.map((record, index) => (
        <FlightLogRecordCard key={`ferry-${index}`} title="Ferry Flight Statement" record={record} />
      ))}

      {extractionResult.removalTags?.map((record, index) => (
        <RemovalTagCard key={`removal-tag-${index}`} record={record} />
      ))}

      {extractionResult.concessionRecords?.map((record, index) => (
        <ConcessionRecordCard key={`concession-${index}`} title="Concession records" record={record} />
      ))}

      {extractionResult.majorRepairRecords?.map((record, index) => (
        <ConcessionRecordCard key={`major-repair-${index}`} title="Major repair / alteration records" record={record} />
      ))}

      <AdTextReferenceCard
        headers={extractionResult.adTextHeaders ?? []}
        references={extractionResult.adTextSbReferences ?? []}
      />

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title={inspected?.title ?? ''}
        data={inspected?.data}
      />
    </div>
  );
}
